import type {
  BuildingGraph,
  BuildingGraphFaceEdge,
  BuildingGraphFaceEdgePlaneIndexItem,
  BuildingGraphFaceNode,
  BuildingGraphFaceNodePlaneIndexItem,
  BuildingGraphFaceOutwardSideDirection,
  BuildingGraphPlanarTopology,
  BuildingGraphPlane,
  BuildingGraphPlaneType,
  BuildingGraphVolumeEdge,
  BuildingGraphVolumeNode,
  BuildingGraphVolumeParentHandle,
} from "building-types/building/buildingGraphTypes";
import type { Building } from "building-types/building/buildingTypes";
import type { ResolvedDatumTable } from "building-types/datums/datumsTypes";
import type { GeneratedFloorBoundaries } from "building-types/floors/floorAssemblyTypes";
import { FloorAssemblyType } from "building-types/floors/floorAssemblyTypes";
import type { FrameFloorAssemblyEntity } from "building-types/floors/frameFloorAssemblyTypes";
import type { SheathingComponentEntity } from "building-types/floors/sheathingComponentTypes";
import type { SlabComponentEntity } from "building-types/floors/slabComponentTypes";
import type { SlabFloorAssemblyEntity } from "building-types/floors/slabFloorAssemblyTypes";
import { FloorFramingComponentType } from "building-types/framing/floorFramingComponentTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type {
  GeneratedFramedFloorAssembly,
  GeneratedSlabFloorAssembly,
} from "building-types/horizontalAssemblies/generatedFloorAssemblyTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { RemoveFormula } from "building-types/options/formula";
import type { RoomIndex } from "building-types/rooms/roomIndex";
import type { Room } from "building-types/rooms/roomTypes";
import type { RoofSettings } from "building-types/settings/roofSettings";
import type { RoofSystemLevel } from "building-types/settings/roofTypes";
import { GableRoofType } from "building-types/settings/settingsTypesEnum";
import type { FreeformWallEntity } from "building-types/walls2/freeformWallTypes";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { resolveInstanceScopedEntityId } from "doc/entity/componentInstanceEntityResolve";
import { evalFormulas, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { isVirtualRoofBlock, resolveBlockRefInstanceId } from "doc/roofSystemTools";
import { getFramingProfileDescriptor } from "doc/utils/lumberTools";
import { MULTIGABLE_DEFAULT_SLOPE } from "doc/utils/roofTools";
import { aabb2ByPolyLine2s, aabb2Grow } from "geom/aabb2";
import type { AABB3 } from "geom/aabb3Types";
import type { Extrusion, Face } from "geom/brep";
import {
  brepAABB3,
  brepNew,
  faceAABB3,
  faceNew,
  faceTopoNew,
  normalizeBRepFaceNormalsOutward,
  transformFaceLoopBasis,
} from "geom/brep";
import { DIST_EPSILON, WindingDir } from "geom/core";
import { extrudeFace, extrusionToBRep } from "geom/extrude";
import { poleOfInaccessibilityPolyLine2 } from "geom/label";
import { planarGraphHalfEdgeStripToPolyLine2, planarGraphVertexStripToPolyLine2 } from "geom/planarGraph";
import {
  planeByThreePoints,
  planeCanonicalFromRefPlane,
  planeCopy,
  planeHash,
  planeIsCoplanar,
  planeNewXY,
  planeNormal,
} from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polygonIntersection, SNAP_ROUNDING_TOLERANCE } from "geom/polyclip/polyClip";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { permutePolygonPlanarGraphFaces, polygonPlanarGraphNew } from "geom/polygonPlanarGraph";
import {
  polyLine2AABB2,
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2EdgeLength,
  polyLine2FromAABB2,
  polyLine2SetCW,
  polyLine2VertexCount,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { vec3AngleBetween, vec3Dot } from "geom/vec3";
import RBush from "rbush";
import { RBush3D } from "rbush-3d";
import {
  faceNodeGetFaceFromTable,
  faceNodeGetVolumeNodeFromTable,
  faceNodeVolumeParentHandle,
} from "../buildingGraph/faceNodeParent";
import { snapOutlineToRoomOutlines } from "../elements/buildingGraph/snapOutlineToRoomOutlines";
import { resolveFreeformFloorTopZ } from "../elements/floors/resolveFloorTopZ";
import { sortedLevels } from "../elements/level/levelTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";
import {
  type GraphCoplanarFacePairIntersection,
  computeCoplanarFacePairIntersection,
  computeCoplanarFacePairsForGenerateBuildingGraph,
  intersectBRepForGenerateBuildingGraph,
} from "./intersectBRepForGenerateBuildingGraph";

type RegisteredBuildingPlane = {
  readonly plane: Plane;
  readonly planeHash: string;
};

/**
 * Register a building plane keyed by exact `planeHash`. Outline snap-to-rooms keeps
 * room/floor/roof wall planes identical at the geometry; hash identity is exact.
 */
function resolveRegisteredBuildingPlane(
  candidate: Plane,
  registry: Map<string, RegisteredBuildingPlane>,
): RegisteredBuildingPlane {
  const candidateHash = planeHash(candidate);
  const existing = registry.get(candidateHash);
  if (existing) {
    return existing;
  }
  const registered: RegisteredBuildingPlane = {
    plane: candidate,
    planeHash: candidateHash,
  };
  registry.set(candidateHash, registered);
  return registered;
}

function roomOutlinesForLevel(levelId: string, roomsByLevel: Table<string[]>, rooms: Table<Room>): PolyLine2[] {
  const roomIds = roomsByLevel[levelId];
  if (!roomIds) {
    return [];
  }
  const outlines: PolyLine2[] = [];
  for (const roomId of roomIds) {
    const outline = rooms[roomId]?.outline;
    if (outline && outline.length >= 6) {
      outlines.push(outline);
    }
  }
  return outlines;
}

const NONDEGENERATE_FACE_COUNT = 6;

function volumeEdgeIdForPair(volumeANodeId: string, volumeBNodeId: string): string {
  const [firstVolumeNodeId, secondVolumeNodeId] =
    volumeANodeId < volumeBNodeId ? [volumeANodeId, volumeBNodeId] : [volumeBNodeId, volumeANodeId];
  return `volume-edge-${firstVolumeNodeId}-${secondVolumeNodeId}`;
}

function parseVolumeEdgePairKey(pairKey: string): { aFaceIndex: number; bFaceIndex: number } | null {
  const [aFaceIndexString, bFaceIndexString] = pairKey.split(":");
  if (!aFaceIndexString || !bFaceIndexString) {
    return null;
  }
  const aFaceIndex = Number(aFaceIndexString);
  const bFaceIndex = Number(bFaceIndexString);
  if (!Number.isFinite(aFaceIndex) || !Number.isFinite(bFaceIndex)) {
    return null;
  }
  return { aFaceIndex, bFaceIndex };
}

/**
 * Stable key for an intersection BRep face: prefer sorted source face-pair provenance
 * over the BRep array index, which can reorder across regenerations.
 */
function stableBoolFaceKey(
  resultFaceIndex: number,
  pairToResultFaceIndices: Map<string, number[]> | undefined,
  face: Face,
): string {
  if (pairToResultFaceIndices && pairToResultFaceIndices.size > 0) {
    const pairKeys: string[] = [];
    for (const [pairKey, resultFaceIndices] of pairToResultFaceIndices) {
      if (resultFaceIndices.includes(resultFaceIndex)) {
        pairKeys.push(pairKey);
      }
    }
    if (pairKeys.length > 0) {
      return pairKeys.sort().join("+");
    }
  }

  // Fallback when provenance is missing: geometric plane identity (not array order).
  return `plane-${planeHash(face.plane)}`;
}

function uniqueBoolFaceNodeId(volumeEdgeId: string, stableKey: string, usedIds: Set<string>, face: Face): string {
  const baseId = `bool-face-${volumeEdgeId}-${stableKey}`;
  if (!usedIds.has(baseId)) {
    usedIds.add(baseId);
    return baseId;
  }

  // Rare collision (e.g. multiple faces sharing a plane-hash fallback): disambiguate geometrically.
  const aabb = faceAABB3(face);
  const centerKey = [
    ((aabb.minX + aabb.maxX) * 0.5).toFixed(3),
    ((aabb.minY + aabb.maxY) * 0.5).toFixed(3),
    ((aabb.minZ + aabb.maxZ) * 0.5).toFixed(3),
  ].join(",");
  const disambiguatedId = `${baseId}@${centerKey}`;
  usedIds.add(disambiguatedId);
  return disambiguatedId;
}

/**
 * Stable topology-ID fingerprint for a face node.
 *
 * - ROOM / ROOF: use the face-node id as-is (doc entity ids are durable).
 * - FLOOR: rewrite bay-keyed ids to the parent floor id so random bay UIDs do not
 *   destabilize selection handles across regenerations.
 * - VOLUME_EDGE: omitted (boolean intersection face ids are unstable).
 */
function topologyIdFingerprintForFaceNode(
  faceNodeId: string,
  faceNodes: Table<BuildingGraphFaceNode>,
  volumeNodes: Table<BuildingGraphVolumeNode>,
): string | undefined {
  const faceNode = faceNodes[faceNodeId];
  if (!faceNode) {
    return undefined;
  }
  const volumeParentHandle = faceNodeVolumeParentHandle(faceNode);
  if (!volumeParentHandle) {
    // Non-volume face nodes (e.g. freeform walls) are keyed by durable doc entity ids.
    return faceNodeId;
  }
  const volumeNode = volumeNodes[volumeParentHandle.volumeNodeId];
  const parentHandle = volumeNode?.parentHandle;
  if (!parentHandle) {
    return faceNodeId;
  }
  if (parentHandle.type === "VOLUME_EDGE") {
    return undefined;
  }
  if (parentHandle.type === "FLOOR") {
    // volume-floor-{levelId}-{boundaryId|bayId}-{perimeterIndex} → fingerprint by parent floor.
    return `face-volume-floor-${parentHandle.levelId}-${parentHandle.floorId}-${volumeParentHandle.volumeNodeFaceIndex}`;
  }
  return faceNodeId;
}

/**
 * Topology IDs fingerprint durable room/roof/floor contributors.
 *
 * VOLUME_EDGE bool-faces still participate in the topology graph, but are omitted from
 * the id. FLOOR bay boundary keys are rewritten to the parent floor id.
 */
function topologyIdContributorFaceNodeIds(
  faceNodeIds: readonly string[],
  faceNodes: Table<BuildingGraphFaceNode>,
  volumeNodes: Table<BuildingGraphVolumeNode>,
): readonly string[] {
  const durableIds = new Set<string>();
  for (const faceNodeId of faceNodeIds) {
    const fingerprint = topologyIdFingerprintForFaceNode(faceNodeId, faceNodes, volumeNodes);
    if (fingerprint !== undefined) {
      durableIds.add(fingerprint);
    }
  }
  if (durableIds.size > 0) {
    return [...durableIds];
  }
  // Degenerate: component is only intersection faces — fall back to full membership.
  return faceNodeIds;
}

function select({
  planarGraphs,
  levels,
  blocks,
  roomsByLevel,
  rooms,
  roomLevelIndices,
  roomIndex,
  floorBoundaries,
  framedFloorAssemblies,
  slabFloorAssemblies,
  resolvedDatums,
  enabledFeatures,
  roofSystemLevels,
  horizontalAssemblyFloors: floors,
  horizontalAssemblyFloorSystems: floorSystems,
  doc: {
    roofs,
    optionSets,
    instanceOptionSets,
    frameFloorAssemblies: docFrameFloorAssemblies,
    slabFloorAssemblies: docSlabFloorAssemblies,
    framingComponents,
    sheathingComponents,
    slabComponents,
    freeformWalls,
  },
  settings,
  optionChoices,
  entityDisabledByGroup,
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  const resolvedOptionChoices = resolveOptionsInFormula(
    { roofSettings: settings.roof, roofs },
    definitions,
    optionChoices,
  );
  return {
    planarGraphs,
    levels,
    blocks,
    roomsByLevel,
    rooms,
    roomLevelIndices,
    roomIndex,
    floorBoundaries,
    framedFloorAssemblies,
    slabFloorAssemblies,
    resolvedDatums,
    floors,
    floorSystems,
    docFrameFloorAssemblies,
    docSlabFloorAssemblies,
    framingComponents,
    sheathingComponents,
    slabComponents,
    // Older serialized docs may omit this table at runtime.
    freeformWalls: freeformWalls ?? {},
    enabledFeatures,
    roofSystemLevels,
    roofs,
    roofSettings: settings.roof,
    optionChoices: resolvedOptionChoices,
    entityDisabledByGroup,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  planarGraphs: "shallow",
  levels: "shallow",
  blocks: "shallow",
  roomsByLevel: "shallow",
  rooms: "shallow",
  roomLevelIndices: "shallow",
  roomIndex: "shallow",
  floorBoundaries: "shallow",
  framedFloorAssemblies: "shallow",
  slabFloorAssemblies: "shallow",
  resolvedDatums: "shallow",
  floors: "shallow",
  floorSystems: "shallow",
  docFrameFloorAssemblies: "shallow",
  docSlabFloorAssemblies: "shallow",
  framingComponents: "shallow",
  sheathingComponents: "shallow",
  slabComponents: "shallow",
  freeformWalls: "shallow",
  enabledFeatures: "shallow",
  roofSystemLevels: "shallow",
  roofs: "shallow",
  roofSettings: "shallow",
  optionChoices: "shallow",
  entityDisabledByGroup: "shallow",
});

function generate(opts: ReturnType<typeof select>): { buildingGraph: BuildingGraph } {
  const {
    planarGraphs,
    levels,
    blocks,
    roomsByLevel,
    rooms,
    roomLevelIndices,
    roomIndex,
    floorBoundaries,
    resolvedDatums,
    enabledFeatures,
    roofSystemLevels,
  } = opts;

  if (!enabledFeatures.wallPlanes) {
    return {
      buildingGraph: {
        volumeNodes: {},
        volumeEdges: [],
        faceNodes: {},
        faceNodesToEdgeIds: {},
        faceEdges: [],
        faceTopologies: {},
        buildingPlanes: {},
      },
    };
  }

  const roomResult = computeRoomNodes(planarGraphs, levels, roomsByLevel, rooms, roomLevelIndices);

  const roofNodesSettings = resolveRoofNodeSettings(opts);
  const roofResult = computeRoofNodes(
    roofSystemLevels,
    levels,
    blocks,
    opts.roofs,
    roofNodesSettings,
    roomsByLevel,
    rooms,
  );
  const floorResult = computeFloorNodes(
    floorBoundaries,
    opts.floors,
    opts.floorSystems,
    opts.framedFloorAssemblies,
    opts.slabFloorAssemblies,
    opts.docFrameFloorAssemblies,
    opts.docSlabFloorAssemblies,
    opts.framingComponents,
    opts.sheathingComponents,
    opts.slabComponents,
    resolvedDatums,
    levels,
    roomsByLevel,
    rooms,
  );
  // Freeform walls have no volume; they contribute face nodes directly and must be
  // merged before face edges, topologies and planes are computed.
  const freeformWallFaceNodes = computeFreeformWallFaceNodes(opts.freeformWalls, levels, opts.entityDisabledByGroup);

  let volumeNodes = { ...roomResult.volumeNodes, ...roofResult.volumeNodes, ...floorResult.volumeNodes };
  let faceNodes = {
    ...roomResult.faceNodes,
    ...roofResult.faceNodes,
    ...floorResult.faceNodes,
    ...freeformWallFaceNodes,
  };

  const { volumeEdges: baseVolumeEdges, graphIntersectionsByVolumeEdgeId } = computeVolumeEdges(
    volumeNodes,
    rooms,
    roomIndex,
  );
  const volumeEdgeNodes = computeVolumeEdgeNodes(baseVolumeEdges, graphIntersectionsByVolumeEdgeId, volumeNodes);
  const allVolumeEdges = mergeVolumeEdgesPreservingParentIndices(
    baseVolumeEdges,
    volumeEdgeNodes.additionalVolumeEdges,
  );

  // update the volume nodes and face nodes with the new volume edge nodes
  volumeNodes = { ...volumeNodes, ...volumeEdgeNodes.volumeNodes };
  faceNodes = { ...faceNodes, ...volumeEdgeNodes.faceNodes };

  const faceEdges = computeFaceEdges(
    allVolumeEdges,
    volumeNodes,
    faceNodes,
    graphIntersectionsByVolumeEdgeId,
    volumeEdgeNodes.syntheticFaceEdgeMappingsByVolumeEdgeId,
  );
  const faceTopologies = computeFaceTopologies(faceEdges, faceNodes, volumeNodes);
  const buildingPlanes = computePlanes(faceTopologies);
  const buildingGraph: BuildingGraph = {
    volumeNodes,
    volumeEdges: allVolumeEdges,
    faceNodes,
    faceNodesToEdgeIds: buildFaceNodesToEdgeIds(faceEdges),
    faceEdges,
    faceTopologies,
    buildingPlanes,
  };

  return { buildingGraph };
}

export default memo(select, generate, compare);

function computeRoomNodes(
  planarGraphs: Table<PolygonPlanarGraph>,
  levels: Table<Level>,
  roomsByLevel: Table<string[]>,
  rooms: Table<Room>,
  roomLevelIndices: Table<number>,
) {
  const volumeNodes: RWTable<BuildingGraphVolumeNode> = {};
  const faceNodes: RWTable<BuildingGraphFaceNode> = {};

  const orderedLevels = sortedLevels(levels);

  for (let levelIdx = 0; levelIdx < orderedLevels.length; levelIdx++) {
    const level = orderedLevels[levelIdx];
    const levelId = level.id;
    const planarGraph = planarGraphs[levelId];

    const roomIds = roomsByLevel[levelId];
    if (!planarGraph || !roomIds) {
      continue;
    }

    const minZ = level.floorElevation - level.floorPlateThickness;
    const maxZ = level.floorElevation + level.topOfWall;
    const extrusionHeight = maxZ - minZ;

    for (const roomId of roomIds) {
      const room = rooms[roomId];
      if (!room) {
        continue;
      }
      const roomLevelIndex = roomLevelIndices[roomId];
      if (roomLevelIndex === undefined) {
        continue;
      }

      const polygonToPlanarGraph = planarGraph.polygonToPlanarGraph;
      const planarGraphFaceRef = polygonToPlanarGraph[roomLevelIndex]?.[0]?.[0];
      if (!planarGraphFaceRef) {
        continue;
      }
      const planarGraphFaceIndex = planarGraphFaceRef.index;

      const outline = polyLine2Copy(room.outline);
      polyLine2SetCW(outline);
      const profileFace = faceNew([outline], planeNewXY(0, 0, minZ));
      const extrusion = extrudeFace(profileFace, 0, 0, extrusionHeight);
      const brep = extrusionToBRep(extrusion);
      // `outwardSideDirection` is derived from face normals, so source BReps
      // must be outward-oriented or OUTWARD/INWARD semantics will invert.
      normalizeBRepFaceNormalsOutward(brep);
      const aabb = brepAABB3(brep);
      const faceAabbs = brep.faces.map((face) => faceAABB3(face));

      const volumeNodeId = `volume-${levelId}-${roomId}`;
      const faceNodeIds: string[] = [];
      const faceNodeIdByFaceIndex: string[] = [];

      for (let faceIdx = 0; faceIdx < brep.faces.length; faceIdx++) {
        const faceNodeId = `face-${volumeNodeId}-${faceIdx}`;
        const face = brep.faces[faceIdx];
        if (!face) {
          continue;
        }
        const normalToZAxisAngle = getFaceNormalToZAxisAngle(face);
        const faceNode: BuildingGraphFaceNode = {
          faceNodeId,
          normalToZAxisAngle,
          outwardSideDirection: getFaceOutwardSideDirection(face),
          parentHandle: { type: "VOLUME", volumeNodeId, volumeNodeFaceIndex: faceIdx },
          aabb: faceAabbs[faceIdx],
        };

        faceNodes[faceNodeId] = faceNode;
        faceNodeIds.push(faceNodeId);
        faceNodeIdByFaceIndex[faceIdx] = faceNodeId;
      }

      const volumeNode: BuildingGraphVolumeNode = {
        volumeNodeId,
        buildingPlanarGraphId: levelId,
        buildingPlanarGraphFaceIndex: planarGraphFaceIndex,
        parentHandle: {
          type: "ROOM",
          roomId,
          levelId,
        },
        brep,
        aabb,
        faceAabbs,
        faceNodeIds,
        faceNodeIdByFaceIndex,
      };

      volumeNodes[volumeNodeId] = volumeNode;
    }
  }

  return { volumeNodes, faceNodes };
}

/** A segment needs both endpoints to produce a wall face. */
const MIN_FREEFORM_WALL_PATH_VERTEX_COUNT = 2;

export function freeformWallFaceNodeId(freeformWallId: string, edgeId: number): string {
  return `face-freeform-wall-${freeformWallId}-${edgeId}`;
}

/**
 * Face nodes for freeform wall path segments.
 *
 * Freeform walls are not enclosed by a volume, so each path segment contributes a
 * single vertical face node spanning the level's wall height. There is intentionally no
 * volume node: face nodes carry their own geometry via `face`.
 */
function computeFreeformWallFaceNodes(
  freeformWalls: Table<FreeformWallEntity>,
  levels: Table<Level>,
  entityDisabledByGroup: Table<boolean> | undefined,
): RWTable<BuildingGraphFaceNode> {
  const faceNodes: RWTable<BuildingGraphFaceNode> = {};

  for (const freeformWallId in freeformWalls) {
    if (entityDisabledByGroup?.[freeformWallId]) {
      continue;
    }
    const freeformWall = freeformWalls[freeformWallId];
    const path = freeformWall?.path;
    if (!freeformWall || !path || polyLine2VertexCount(path) < MIN_FREEFORM_WALL_PATH_VERTEX_COUNT) {
      continue;
    }

    const level = levels[freeformWall.levelId];
    if (!level) {
      continue;
    }
    const minZ = level.floorElevation - level.floorPlateThickness;
    const maxZ = level.floorElevation + level.topOfWall;
    const wallHeight = maxZ - minZ;
    if (wallHeight <= DIST_EPSILON) {
      continue;
    }

    // `edgeIds` is positional and authored alongside `path`; only emit face nodes for
    // segments that have a corresponding doc edge so `edgeId` always resolves.
    const edgeCount = Math.min(polyLine2EdgeCount(path), freeformWall.edgeIds.length);
    for (let edgeId = 0; edgeId < edgeCount; edgeId++) {
      const face = freeformWallSegmentFace(path, edgeId, minZ, wallHeight);
      if (!face) {
        continue;
      }
      const faceNodeId = freeformWallFaceNodeId(freeformWallId, edgeId);
      faceNodes[faceNodeId] = {
        faceNodeId,
        normalToZAxisAngle: getFaceNormalToZAxisAngle(face),
        outwardSideDirection: getFaceOutwardSideDirection(face),
        parentHandle: {
          type: "FREEFORM_WALL",
          freeformWallId,
          edgeId,
        },
        face,
        aabb: faceAABB3(face),
      };
    }
  }

  return faceNodes;
}

/**
 * Vertical rectangular face for one wall path segment.
 *
 * The plane origin is the segment's end vertex, U points back to the start vertex and V
 * points up, so the face normal (U × V) points to the LEFT of the directed segment. That
 * puts the face node's outward side on the left of the path traversed vertex 0 → N, which
 * is the {@link FreeformWallEntity} orientation contract: left is the wall-plane
 * positive-normal side, right is the negative-normal side.
 */
function freeformWallSegmentFace(path: PolyLine2, edgeId: number, minZ: number, wallHeight: number): Face | null {
  const segmentLength = polyLine2EdgeLength(path, edgeId);
  if (segmentLength <= DIST_EPSILON) {
    return null;
  }

  const startIndex = edgeId * 2;
  const startX = path[startIndex];
  const startY = path[startIndex + 1];
  const endX = path[startIndex + 2];
  const endY = path[startIndex + 3];

  const plane = planeByThreePoints(endX, endY, minZ, startX, startY, minZ, endX, endY, minZ + wallHeight);
  if (!plane) {
    return null;
  }

  const loop = polyLine2New([0, 0, segmentLength, 0, segmentLength, wallHeight, 0, wallHeight, 0, 0]);
  polyLine2SetCW(loop);
  return faceNew([loop], plane);
}

function computeFloorNodes(
  floorBoundaries: Table<GeneratedFloorBoundaries>,
  floors: Building["doc"]["floors"],
  floorSystems: Building["doc"]["floorSystems"],
  framedFloorAssemblies: Table<GeneratedFramedFloorAssembly>,
  slabFloorAssemblies: Table<GeneratedSlabFloorAssembly>,
  docFrameFloorAssemblies: Table<FrameFloorAssemblyEntity>,
  docSlabFloorAssemblies: Table<SlabFloorAssemblyEntity>,
  framingComponents: Table<FramingComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
  slabComponents: Table<SlabComponentEntity>,
  resolvedDatums: ResolvedDatumTable,
  levels: Table<Level>,
  roomsByLevel: Table<string[]>,
  rooms: Table<Room>,
) {
  const volumeNodes: RWTable<BuildingGraphVolumeNode> = {};
  const faceNodes: RWTable<BuildingGraphFaceNode> = {};
  const firstLevel = sortedLevels(levels)[0];
  const roomOutlinesByLevel: RWTable<PolyLine2[]> = {};

  for (const boundaryId in floorBoundaries) {
    const floorBoundary = floorBoundaries[boundaryId];
    if (!floorBoundary || floorBoundary.perimeters.length === 0) {
      continue;
    }

    const floorId = floorBoundary.parentFloorId ?? boundaryId;
    const floor = floors[floorId];
    const levelId = floor?.levelId ?? firstLevel?.id;
    const level = levelId ? levels[levelId] : undefined;
    if (!levelId || !level) {
      continue;
    }

    const floorAssemblyDepth =
      resolveFloorAssemblyDepth(
        boundaryId,
        floorBoundary.bayId,
        floor,
        floorSystems,
        framedFloorAssemblies,
        slabFloorAssemblies,
        docFrameFloorAssemblies,
        docSlabFloorAssemblies,
        framingComponents,
        sheathingComponents,
        slabComponents,
      ) ?? level.floorPlateThickness;
    const maxZ = resolveFreeformFloorTopZ(floor, floorSystems, level, resolvedDatums);
    const minZ = maxZ - floorAssemblyDepth;
    const extrusionHeight = maxZ - minZ;
    if (extrusionHeight <= DIST_EPSILON) {
      continue;
    }

    let levelRoomOutlines = roomOutlinesByLevel[levelId];
    if (!levelRoomOutlines) {
      levelRoomOutlines = roomOutlinesForLevel(levelId, roomsByLevel, rooms);
      roomOutlinesByLevel[levelId] = levelRoomOutlines;
    }

    for (let perimeterIndex = 0; perimeterIndex < floorBoundary.perimeters.length; perimeterIndex++) {
      const perimeter = floorBoundary.perimeters[perimeterIndex];
      if (!perimeter || perimeter.length < NONDEGENERATE_FACE_COUNT) {
        continue;
      }

      // Graph-local snap only — do not mutate floorBoundaries used by framing.
      const profileOutline = polyLine2SetCW(snapOutlineToRoomOutlines(perimeter, levelRoomOutlines));
      const profileFace = faceNew([profileOutline], planeNewXY(0, 0, minZ));
      const extrusion = extrudeFace(profileFace, 0, 0, extrusionHeight);
      const brep = extrusionToBRep(extrusion);
      normalizeBRepFaceNormalsOutward(brep);

      const aabb = brepAABB3(brep);
      const faceAabbs = brep.faces.map((face) => faceAABB3(face));
      const volumeNodeId = `volume-floor-${levelId}-${boundaryId}-${perimeterIndex}`;
      const faceNodeIds: string[] = [];
      const faceNodeIdByFaceIndex: string[] = [];

      for (let faceIdx = 0; faceIdx < brep.faces.length; faceIdx++) {
        const face = brep.faces[faceIdx];
        if (!face) {
          continue;
        }
        const faceNodeId = `face-${volumeNodeId}-${faceIdx}`;
        faceNodes[faceNodeId] = {
          faceNodeId,
          normalToZAxisAngle: getFaceNormalToZAxisAngle(face),
          outwardSideDirection: getFaceOutwardSideDirection(face),
          parentHandle: { type: "VOLUME", volumeNodeId, volumeNodeFaceIndex: faceIdx },
          aabb: faceAabbs[faceIdx],
        };
        faceNodeIds.push(faceNodeId);
        faceNodeIdByFaceIndex[faceIdx] = faceNodeId;
      }

      volumeNodes[volumeNodeId] = {
        volumeNodeId,
        buildingPlanarGraphId: levelId,
        buildingPlanarGraphFaceIndex: -1,
        parentHandle: {
          type: "FLOOR",
          floorId,
          levelId,
        },
        brep,
        aabb,
        faceAabbs,
        faceNodeIds,
        faceNodeIdByFaceIndex,
      };
    }
  }

  return { volumeNodes, faceNodes };
}

function resolveFloorAssemblyDepth(
  boundaryId: string,
  bayId: string | undefined,
  floor: Building["doc"]["floors"][string] | undefined,
  floorSystems: Building["doc"]["floorSystems"],
  framedFloorAssemblies: Table<GeneratedFramedFloorAssembly>,
  slabFloorAssemblies: Table<GeneratedSlabFloorAssembly>,
  docFrameFloorAssemblies: Table<FrameFloorAssemblyEntity>,
  docSlabFloorAssemblies: Table<SlabFloorAssemblyEntity>,
  framingComponents: Table<FramingComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
  slabComponents: Table<SlabComponentEntity>,
): number | undefined {
  const generatedFramedFloorAssembly = framedFloorAssemblies[boundaryId];
  if (generatedFramedFloorAssembly) {
    const generatedFramedAssemblyDepth = resolveGeneratedFramedFloorAssemblyDepth(generatedFramedFloorAssembly);
    return generatedFramedAssemblyDepth > DIST_EPSILON ? generatedFramedAssemblyDepth : undefined;
  }

  const generatedSlabFloorAssembly = slabFloorAssemblies[boundaryId];
  if (generatedSlabFloorAssembly) {
    const generatedSlabAssemblyDepth = resolveGeneratedSlabFloorAssemblyDepth(generatedSlabFloorAssembly);
    return generatedSlabAssemblyDepth > DIST_EPSILON ? generatedSlabAssemblyDepth : undefined;
  }

  if (!floor) {
    return undefined;
  }
  const floorSystem = floorSystems[floor.parentId];
  const bayAssemblyId = bayId ? floor.splitData?.bays?.[bayId]?.assemblyId : undefined;
  const floorAssemblyId = floor.assemblyType === FloorAssemblyType.SOLID ? floor.assemblyId : undefined;
  const assemblyId = bayAssemblyId ?? floorAssemblyId ?? floorSystem?.assemblyId;
  if (!assemblyId) {
    return undefined;
  }

  const frameFloorAssembly = docFrameFloorAssemblies[assemblyId];
  if (frameFloorAssembly) {
    // @TODO WALLS V2 - maybe use joist component for depth calc
    // needs product input
    // const joistComponent = framingComponents[frameFloorAssembly.joistComponentId];

    const subFloorComponent = sheathingComponents[frameFloorAssembly.subFloorComponentId];
    const rimComponent = framingComponents[frameFloorAssembly.rimComponentId];

    const rimDepth = rimComponent ? getFramingProfileDescriptor(rimComponent).width : 0;
    const subfloorDepth = subFloorComponent?.thickness ?? 0;
    const assemblyDepth = rimDepth + subfloorDepth;
    return assemblyDepth > DIST_EPSILON ? assemblyDepth : undefined;
  }

  const slabFloorAssembly = docSlabFloorAssemblies[assemblyId];
  if (slabFloorAssembly) {
    const slabComponent = slabComponents[slabFloorAssembly.slabComponentId];
    const subFloorComponent = sheathingComponents[slabFloorAssembly.subFloorComponentId];
    const structuralDepth = slabComponent?.thickness ?? 0;
    const subfloorDepth = subFloorComponent?.thickness ?? 0;
    const assemblyDepth = structuralDepth + subfloorDepth;
    return assemblyDepth > DIST_EPSILON ? assemblyDepth : undefined;
  }

  return undefined;
}

function resolveGeneratedFramedFloorAssemblyDepth(assembly: GeneratedFramedFloorAssembly): number {
  let rimDepth: number | undefined;
  let joistDepth: number | undefined;

  for (const islandFraming of assembly.framing) {
    for (const member of islandFraming) {
      if (member.type === FloorFramingComponentType.RIM_JOIST) {
        rimDepth =
          rimDepth !== undefined ? Math.max(rimDepth, member.profileDescriptor.width) : member.profileDescriptor.width;
      }
      if (member.type === FloorFramingComponentType.FLOOR_JOIST) {
        joistDepth =
          joistDepth !== undefined
            ? Math.max(joistDepth, member.profileDescriptor.width)
            : member.profileDescriptor.width;
      }
    }
  }

  const structuralDepth = rimDepth ?? joistDepth ?? 0;
  const subfloorDepth = assembly.subfloorThickness ?? resolveExtrusionsThickness(assembly.subfloor);
  return structuralDepth + subfloorDepth;
}

function resolveGeneratedSlabFloorAssemblyDepth(assembly: GeneratedSlabFloorAssembly): number {
  const structuralDepth = resolveExtrusionsThickness(assembly.slab);
  const subfloorDepth = assembly.subfloorThickness ?? resolveExtrusionsThickness(assembly.subfloor);
  return structuralDepth + subfloorDepth;
}

function resolveExtrusionsThickness(extrusions: Extrusion[]): number {
  let thickness = 0;
  for (const extrusion of extrusions) {
    thickness = Math.max(thickness, Math.abs(extrusion.dirZ));
  }
  return thickness;
}

interface RoofNodeSettings {
  readonly roofSettings: RemoveFormula<RoofSettings>;
  readonly roofInstanceSettings: Table<RemoveFormula<RoofSettings>>;
}

function resolveRoofNodeSettings(opts: ReturnType<typeof select>): RoofNodeSettings {
  const roofSettings = evalFormulas(
    opts.roofSettings,
    (v) => opts.optionChoices[v],
    undefined,
    opts.entityDisabledByGroup,
  ) as RemoveFormula<RoofSettings>;

  const roofInstanceSettings: RWTable<RemoveFormula<RoofSettings>> = {};
  for (const roofId in opts.roofs) {
    const roof = opts.roofs[roofId];
    if (!roof?.roofSettings) {
      continue;
    }
    roofInstanceSettings[roofId] = evalFormulas(
      roof.roofSettings,
      (v) => opts.optionChoices[v],
      undefined,
      opts.entityDisabledByGroup,
    ) as RemoveFormula<RoofSettings>;
  }

  return { roofSettings, roofInstanceSettings };
}

function computeRoofNodes(
  roofSystemLevels: Table<RoofSystemLevel>,
  levels: Table<Level>,
  blocks: Building["blocks"],
  roofs: Building["doc"]["roofs"],
  computeRoofNodesSettings: RoofNodeSettings,
  roomsByLevel: Table<string[]>,
  rooms: Table<Room>,
) {
  const { roofSettings, roofInstanceSettings } = computeRoofNodesSettings;

  const volumeNodes: RWTable<BuildingGraphVolumeNode> = {};
  const faceNodes: RWTable<BuildingGraphFaceNode> = {};
  const roomOutlinesByLevel: RWTable<PolyLine2[]> = {};

  for (const levelId in roofSystemLevels) {
    const roofSystemLevel = roofSystemLevels[levelId];
    if (!roofSystemLevel) {
      continue;
    }

    for (const roofId in roofSystemLevel.roofPerimeters) {
      const inputPerimeters = roofSystemLevel.roofPerimeters[roofId];
      const roofLevelId = getRoofBaseLevelId({
        roofId,
        fallbackLevelId: levelId,
        roofs,
        blocks,
        levels,
      });
      const roofLevel = levels[roofLevelId];
      if (!roofLevel) {
        continue;
      }

      let levelRoomOutlines = roomOutlinesByLevel[roofLevelId];
      if (!levelRoomOutlines) {
        levelRoomOutlines = roomOutlinesForLevel(roofLevelId, roomsByLevel, rooms);
        roomOutlinesByLevel[roofLevelId] = levelRoomOutlines;
      }

      const baseHeightApprox = roofLevel.floorElevation + roofLevel.topOfWall;
      // Graph-local snap only — overhang vertices farther than tol stay as authored.
      const footprint = snapOutlineToRoomOutlines(inputPerimeters.boundingPerimeter, levelRoomOutlines);
      polyLine2SetCW(footprint);

      const span = getMaxRoofSpan(footprint);
      if (span <= DIST_EPSILON) {
        continue;
      }

      const resolvedRoofSettings = roofInstanceSettings[roofId];
      const roofHeightOffset = resolvedRoofSettings?.eaveFasciaHeightOffset ?? 0;
      const slopeApprox = getApproximateRoofSlope(resolvedRoofSettings, roofSettings);
      const runApprox = isShedLikeRoof(resolvedRoofSettings, roofSettings) ? span : span * 0.5;
      const extrusionHeight = roofHeightOffset + Math.max(slopeApprox * runApprox, DIST_EPSILON);

      const profileFace = faceNew([footprint], planeNewXY(0, 0, baseHeightApprox));
      const extrusion = extrudeFace(profileFace, 0, 0, extrusionHeight);
      const brep = extrusionToBRep(extrusion);
      // `outwardSideDirection` is derived from face normals, so source BReps
      // must be outward-oriented or OUTWARD/INWARD semantics will invert.
      normalizeBRepFaceNormalsOutward(brep);
      const aabb = brepAABB3(brep);

      const faceAabbs = brep.faces.map((face) => faceAABB3(face));

      const planarGraphFaceIndex = roofSystemLevel.roofIdToPolygonIndex[roofId] ?? -1;
      const volumeNodeId = `volume-roof-${levelId}-${roofId}`;
      const faceNodeIds: string[] = [];
      const faceNodeIdByFaceIndex: string[] = [];

      for (let faceIdx = 0; faceIdx < brep.faces.length; faceIdx++) {
        const face = brep.faces[faceIdx];
        if (!face) {
          continue;
        }

        const faceNodeId = `face-${volumeNodeId}-${faceIdx}`;
        const faceNode: BuildingGraphFaceNode = {
          faceNodeId,
          normalToZAxisAngle: getFaceNormalToZAxisAngle(face),
          outwardSideDirection: getFaceOutwardSideDirection(face),
          parentHandle: { type: "VOLUME", volumeNodeId, volumeNodeFaceIndex: faceIdx },
          aabb: faceAabbs[faceIdx],
        };

        faceNodes[faceNodeId] = faceNode;
        faceNodeIds.push(faceNodeId);
        faceNodeIdByFaceIndex[faceIdx] = faceNodeId;
      }

      const volumeNode: BuildingGraphVolumeNode = {
        volumeNodeId,
        buildingPlanarGraphId: levelId,
        buildingPlanarGraphFaceIndex: planarGraphFaceIndex,
        parentHandle: {
          type: "ROOF",
          roofId,
          levelId: roofLevelId,
        },
        brep,
        aabb,
        faceAabbs,
        faceNodeIds,
        faceNodeIdByFaceIndex,
      };

      volumeNodes[volumeNodeId] = volumeNode;
    }
  }

  return { volumeNodes, faceNodes };
}

function getRoofBaseLevelId({
  roofId,
  fallbackLevelId,
  roofs,
  blocks,
  levels,
}: {
  readonly roofId: string;
  readonly fallbackLevelId: string;
  readonly roofs: Building["doc"]["roofs"];
  readonly blocks: Building["blocks"];
  readonly levels: Table<Level>;
}): string {
  const roof = roofs[roofId];
  if (!roof) {
    return fallbackLevelId;
  }

  const roofBlocks = roof.blocks;
  if (isVirtualRoofBlock(roofBlocks)) {
    const resolvedLevelId = resolveInstanceScopedEntityId({
      path: roofBlocks.levelUsage,
      table: levels,
      fallbackId: roofBlocks.levelUsage.entityId,
    });
    return levels[resolvedLevelId] ? resolvedLevelId : fallbackLevelId;
  }

  if (Array.isArray(roofBlocks)) {
    const firstBlockId = roofBlocks[0];
    const blockLevelId = firstBlockId ? blocks[resolveBlockRefInstanceId(firstBlockId, blocks)]?.levelId : undefined;
    return blockLevelId && levels[blockLevelId] ? blockLevelId : fallbackLevelId;
  }

  const blockLevelId = blocks[resolveBlockRefInstanceId(roofBlocks, blocks)]?.levelId;
  return blockLevelId && levels[blockLevelId] ? blockLevelId : fallbackLevelId;
}

function isShedLikeRoof(
  roofSettings: Partial<RemoveFormula<RoofSettings>> | undefined,
  homeRoofSettings: RemoveFormula<RoofSettings>,
): boolean {
  const roofType = roofSettings?.roofType ?? homeRoofSettings.roofType;
  if (roofType === undefined) {
    return false;
  }
  return roofType !== GableRoofType.DEFAULT;
}

function getApproximateRoofSlope(
  roofSettings: Partial<RemoveFormula<RoofSettings>> | undefined,
  homeRoofSettings: RemoveFormula<RoofSettings>,
): number {
  const slopeCandidates = [
    homeRoofSettings.slope,
    homeRoofSettings.rightSlope,
    roofSettings?.slope,
    roofSettings?.rightSlope,
  ];

  let bestSlope = Number.NEGATIVE_INFINITY;
  for (const slopeCandidate of slopeCandidates) {
    if (typeof slopeCandidate !== "number" || !Number.isFinite(slopeCandidate)) {
      continue;
    }
    bestSlope = Math.max(bestSlope, slopeCandidate);
  }

  return Number.isFinite(bestSlope) && bestSlope > DIST_EPSILON ? bestSlope : MULTIGABLE_DEFAULT_SLOPE;
}

function getMaxRoofSpan(perimeter: PolyLine2): number {
  let maxSpan = 0;
  const edgeCount = polyLine2EdgeCount(perimeter);
  for (let edgeIndex = 0; edgeIndex < edgeCount; edgeIndex++) {
    const edgeLength = polyLine2EdgeLength(perimeter, edgeIndex);
    if (edgeLength > maxSpan) {
      maxSpan = edgeLength;
    }
  }
  return maxSpan;
}

/**
 * Computes intersection volume nodes and face nodes for overlapping volume edges,
 * and builds synthetic-edge face mapping data used by computeFaceEdges.
 */
function computeVolumeEdgeNodes(
  volumeEdges: BuildingGraphVolumeEdge[],
  graphIntersectionsByVolumeEdgeId: Map<string, BuildTimeVolumeGraphIntersection>,
  sourceVolumeNodes: Table<BuildingGraphVolumeNode>,
): {
  volumeNodes: RWTable<BuildingGraphVolumeNode>;
  faceNodes: RWTable<BuildingGraphFaceNode>;
  additionalVolumeEdges: BuildingGraphVolumeEdge[];
  syntheticFaceEdgeMappingsByVolumeEdgeId: Map<string, BuildTimeSyntheticFaceEdgeMapping[]>;
} {
  const volumeNodes: RWTable<BuildingGraphVolumeNode> = {};
  const faceNodes: RWTable<BuildingGraphFaceNode> = {};
  const additionalVolumeEdges: BuildingGraphVolumeEdge[] = [];
  const syntheticFaceEdgeMappingsByVolumeEdgeId = new Map<string, BuildTimeSyntheticFaceEdgeMapping[]>();

  for (let volumeEdgeIndex = 0; volumeEdgeIndex < volumeEdges.length; volumeEdgeIndex++) {
    const volumeEdge = volumeEdges[volumeEdgeIndex];
    if (volumeEdge.brep) {
      const volumeNodeId = `volume-intersection-${volumeEdge.volumeEdgeId}`;
      const faceNodeIds: string[] = [];
      const faceNodeIdByFaceIndex: string[] = [];
      const faceAabbs: AABB3[] = [];
      const graphIntersection = graphIntersectionsByVolumeEdgeId.get(volumeEdge.volumeEdgeId);
      const usedBoolFaceNodeIds = new Set<string>();
      for (let faceIdx = 0; faceIdx < volumeEdge.brep.faces.length; faceIdx++) {
        const face = volumeEdge.brep.faces[faceIdx];
        // Prefer source face-pair provenance over BRep faceIdx so topology IDs stay stable
        // when boolean face enumeration order changes across regenerations.
        const faceNodeId = uniqueBoolFaceNodeId(
          volumeEdge.volumeEdgeId,
          stableBoolFaceKey(faceIdx, graphIntersection?.pairToResultFaceIndices, face),
          usedBoolFaceNodeIds,
          face,
        );
        const faceAabb = faceAABB3(face);

        faceNodes[faceNodeId] = {
          faceNodeId,
          normalToZAxisAngle: getFaceNormalToZAxisAngle(face),
          outwardSideDirection: getFaceOutwardSideDirection(face),
          aabb: faceAabb,
          parentHandle: { type: "VOLUME", volumeNodeId, volumeNodeFaceIndex: faceIdx },
        };

        faceNodeIds.push(faceNodeId);
        faceNodeIdByFaceIndex[faceIdx] = faceNodeId;
        faceAabbs.push(faceAabb);
      }

      volumeNodes[volumeNodeId] = {
        volumeNodeId,
        buildingPlanarGraphId: "",
        buildingPlanarGraphFaceIndex: -1,
        parentHandle: {
          type: "VOLUME_EDGE",
          volumeEdgeIndex,
        },
        faceAabbs,
        brep: volumeEdge.brep,
        aabb: brepAABB3(volumeEdge.brep),
        faceNodeIds,
        faceNodeIdByFaceIndex,
      };

      if (graphIntersection) {
        const syntheticMappingsForA: BuildTimeSyntheticFaceEdgeMapping[] = [];
        const syntheticMappingsForB: BuildTimeSyntheticFaceEdgeMapping[] = [];
        const sourceVolumeA = sourceVolumeNodes[volumeEdge.volumeANodeId];
        const sourceVolumeB = sourceVolumeNodes[volumeEdge.volumeBNodeId];
        const sourceBRepA = sourceVolumeA?.brep;
        const sourceBRepB = sourceVolumeB?.brep;
        for (const [pairKey, resultFaceIndices] of graphIntersection.pairToResultFaceIndices) {
          const parsedPair = parseVolumeEdgePairKey(pairKey);
          if (!parsedPair) {
            continue;
          }
          const sourceFaceA = sourceBRepA?.faces[parsedPair.aFaceIndex];
          const sourceFaceB = sourceBRepB?.faces[parsedPair.bFaceIndex];
          for (const resultFaceIndex of resultFaceIndices) {
            const intersectionFaceNodeId = faceNodeIdByFaceIndex[resultFaceIndex];
            const intersectionFace = volumeEdge.brep.faces[resultFaceIndex];
            if (!intersectionFaceNodeId || !intersectionFace) {
              continue;
            }
            if (sourceFaceA && planeIsCoplanar(intersectionFace.plane, sourceFaceA.plane, SNAP_ROUNDING_TOLERANCE)) {
              syntheticMappingsForA.push({
                intersectionFaceNodeId,
                sourceFaceIndex: parsedPair.aFaceIndex,
                faceEdgeDot: 1,
                faceEdgeType: "OVERLAP",
                face: intersectionFace,
              });
            }
            if (sourceFaceB && planeIsCoplanar(intersectionFace.plane, sourceFaceB.plane, SNAP_ROUNDING_TOLERANCE)) {
              syntheticMappingsForB.push({
                intersectionFaceNodeId,
                sourceFaceIndex: parsedPair.bFaceIndex,
                faceEdgeDot: 1,
                faceEdgeType: "OVERLAP",
                face: intersectionFace,
              });
            }
          }
        }
        syntheticFaceEdgeMappingsByVolumeEdgeId.set(`${volumeEdge.volumeEdgeId}-a`, syntheticMappingsForA);
        syntheticFaceEdgeMappingsByVolumeEdgeId.set(`${volumeEdge.volumeEdgeId}-b`, syntheticMappingsForB);
      }

      additionalVolumeEdges.push({
        volumeEdgeId: `${volumeEdge.volumeEdgeId}-a`,
        volumeANodeId: volumeNodeId,
        volumeBNodeId: volumeEdge.volumeANodeId,
        /**
         * @NOTE - no brep here, since the brep is the intersection of the two volumes
         */
      });

      additionalVolumeEdges.push({
        volumeEdgeId: `${volumeEdge.volumeEdgeId}-b`,
        volumeANodeId: volumeNodeId,
        volumeBNodeId: volumeEdge.volumeBNodeId,
        /**
         * @NOTE - no brep here, since the brep is the intersection of the two volumes
         */
      });
    }
  }

  return { volumeNodes, faceNodes, additionalVolumeEdges, syntheticFaceEdgeMappingsByVolumeEdgeId };
}

interface VolumeNodeSearchItem extends AABB3 {
  index: number;
}

interface BuildTimeVolumeGraphIntersection {
  readonly facePairs: GraphCoplanarFacePairIntersection[];
  readonly pairToResultFaceIndices: Map<string, number[]>;
}

interface BuildTimeSyntheticFaceEdgeMapping {
  readonly intersectionFaceNodeId: string;
  readonly sourceFaceIndex: number;
  readonly faceEdgeType: "OVERLAP" | "EDGE";
  readonly faceEdgeDot: number;
  readonly face?: Face;
}

/**
 * RBush3D broad-phase check across all volume AABBs.
 *
 * We intentionally do not query room AABBs from BuildingGenerationContext yet.
 * The room index AABBs are generated in roomNew() for room/query semantics
 * (room-class base offsets), while this graph stage uses extruded room volumes
 * that include floor plate thickness. Reusing the room index here can change
 * overlap candidates and regress downstream behavior that depends on current
 * room-index semantics.
 */
type RoomVolumeParentHandle = Extract<BuildingGraphVolumeParentHandle, { type: "ROOM" }>;

function getSameLevelRoomVolumePair(
  nodeA: BuildingGraphVolumeNode,
  nodeB: BuildingGraphVolumeNode,
): { handleA: RoomVolumeParentHandle; handleB: RoomVolumeParentHandle } | undefined {
  const handleA = nodeA.parentHandle;
  const handleB = nodeB.parentHandle;
  if (handleA?.type !== "ROOM" || handleB?.type !== "ROOM") {
    return undefined;
  }
  if (handleA.levelId !== handleB.levelId) {
    return undefined;
  }
  return { handleA, handleB };
}

function sameLevelRoomsShareWall(
  roomIdA: string,
  roomIdB: string,
  rooms: Table<Room>,
  roomIndex: RoomIndex,
  adjacentRoomIdsByRoomId: Map<string, Set<string>>,
): boolean {
  let adjacentRoomIds = adjacentRoomIdsByRoomId.get(roomIdA);
  if (!adjacentRoomIds) {
    adjacentRoomIds = new Set<string>();
    const roomA = rooms[roomIdA];
    if (roomA) {
      for (const adjacentRoom of roomIndex.adjacentRoomsFlattened(roomA)) {
        adjacentRoomIds.add(adjacentRoom.id);
      }
    }
    adjacentRoomIdsByRoomId.set(roomIdA, adjacentRoomIds);
  }

  return adjacentRoomIds.has(roomIdB);
}

function intersectVolumePairForBuildingGraph(
  nodeA: BuildingGraphVolumeNode,
  nodeB: BuildingGraphVolumeNode,
): ReturnType<typeof intersectBRepForGenerateBuildingGraph> {
  const roomPair = getSameLevelRoomVolumePair(nodeA, nodeB);
  if (roomPair) {
    // Same-level room volumes are non-overlapping in plan (see generatePlanarGraphs) and only
    // need coplanar face-pair adjacency when they share a wall via the planar graph.
    return computeCoplanarFacePairsForGenerateBuildingGraph(nodeA.brep, nodeB.brep);
  }

  return intersectBRepForGenerateBuildingGraph(nodeA.brep, nodeB.brep);
}

function computeVolumeEdges(
  volumeNodes: Table<BuildingGraphVolumeNode>,
  rooms: Table<Room>,
  roomIndex: RoomIndex,
): {
  volumeEdges: BuildingGraphVolumeEdge[];
  graphIntersectionsByVolumeEdgeId: Map<string, BuildTimeVolumeGraphIntersection>;
} {
  const edges: BuildingGraphVolumeEdge[] = [];
  const graphIntersectionsByVolumeEdgeId = new Map<string, BuildTimeVolumeGraphIntersection>();
  const adjacentRoomIdsByRoomId = new Map<string, Set<string>>();
  const nodes = Object.values(volumeNodes);
  const rtree = new RBush3D();
  const searchItems: VolumeNodeSearchItem[] = nodes.map((node, index) => ({
    ...node.aabb,
    index,
  }));
  rtree.load(searchItems);

  for (let i = 0; i < nodes.length; i++) {
    const nodeA = nodes[i];
    const searchAABB: AABB3 = {
      minX: nodeA.aabb.minX - DIST_EPSILON,
      minY: nodeA.aabb.minY - DIST_EPSILON,
      minZ: nodeA.aabb.minZ - DIST_EPSILON,
      maxX: nodeA.aabb.maxX + DIST_EPSILON,
      maxY: nodeA.aabb.maxY + DIST_EPSILON,
      maxZ: nodeA.aabb.maxZ + DIST_EPSILON,
    };
    const candidateIndices = (rtree.search(searchAABB) as VolumeNodeSearchItem[])
      .map((item) => item.index)
      .filter((index) => index > i)
      .sort((a, b) => a - b);

    for (const j of candidateIndices) {
      const nodeB = nodes[j];
      if (!nodeB) {
        continue;
      }

      const roomPair = getSameLevelRoomVolumePair(nodeA, nodeB);
      if (
        roomPair &&
        !sameLevelRoomsShareWall(
          roomPair.handleA.roomId,
          roomPair.handleB.roomId,
          rooms,
          roomIndex,
          adjacentRoomIdsByRoomId,
        )
      ) {
        continue;
      }

      const graphIntersection = intersectVolumePairForBuildingGraph(nodeA, nodeB);
      const intersectionFaces = graphIntersection.intersectionFaces;
      const volumeEdgeId = volumeEdgeIdForPair(nodeA.volumeNodeId, nodeB.volumeNodeId);
      const brep =
        intersectionFaces.length > 0
          ? brepNew(
              intersectionFaces,
              intersectionFaces.map(() => faceTopoNew()),
            )
          : undefined;
      if (brep) {
        // Boolean intersection BReps must also be normalized to keep
        // outward/inward face-side classification stable.
        normalizeBRepFaceNormalsOutward(brep);
      }

      edges.push({
        volumeEdgeId,
        volumeANodeId: nodeA.volumeNodeId,
        volumeBNodeId: nodeB.volumeNodeId,
        brep,
      });
      graphIntersectionsByVolumeEdgeId.set(volumeEdgeId, {
        facePairs: graphIntersection.facePairs,
        pairToResultFaceIndices: graphIntersection.pairToResultFaceIndices,
      });
    }
  }

  return { volumeEdges: edges, graphIntersectionsByVolumeEdgeId };
}

/**
 * Compute face edges from precomputed build-time mappings.
 * Synthetic `${baseId}-a|b` edges consume provenance-derived mappings first;
 * base edges without brep consume precomputed coplanar `facePairs`.
 * Then complete the graph for intersection volumes: when parent faces have an
 * opposing OVERLAP edge, emit opposing OVERLAP edges between their intersection
 * child faces that still overlap in area.
 */
function computeFaceEdges(
  volumeEdges: BuildingGraphVolumeEdge[],
  volumeNodes: Table<BuildingGraphVolumeNode>,
  faceNodes: Table<BuildingGraphFaceNode>,
  graphIntersectionsByVolumeEdgeId: Map<string, BuildTimeVolumeGraphIntersection>,
  syntheticFaceEdgeMappingsByVolumeEdgeId: Map<string, BuildTimeSyntheticFaceEdgeMapping[]>,
): BuildingGraphFaceEdge[] {
  const edges: BuildingGraphFaceEdge[] = [];
  const seen = new Set<string>();

  for (const volumeEdge of volumeEdges) {
    const volA = volumeNodes[volumeEdge.volumeANodeId];
    const volB = volumeNodes[volumeEdge.volumeBNodeId];
    if (!volA || !volB) {
      continue;
    }

    // Synthetic mappings exist only for synthetic edges (`${baseId}-a` / `${baseId}-b`)
    // created from a base edge that had an intersection brep. If this lookup misses,
    // we're either on an original/base edge, or on a synthetic edge whose base pair
    // produced no attributable coplanar mappings after provenance filtering.
    const syntheticMappings = syntheticFaceEdgeMappingsByVolumeEdgeId.get(volumeEdge.volumeEdgeId);
    if (syntheticMappings) {
      for (const syntheticMapping of syntheticMappings) {
        const faceNodeIdA = syntheticMapping.intersectionFaceNodeId;
        const faceNodeIdB = volB.faceNodeIdByFaceIndex[syntheticMapping.sourceFaceIndex];
        if (!faceNodeIdA || !faceNodeIdB) {
          continue;
        }
        const edgeKey = faceNodeIdA < faceNodeIdB ? `${faceNodeIdA}-${faceNodeIdB}` : `${faceNodeIdB}-${faceNodeIdA}`;
        if (seen.has(edgeKey)) {
          continue;
        }
        seen.add(edgeKey);
        edges.push({
          faceANodeId: faceNodeIdA,
          faceBNodeId: faceNodeIdB,
          face: syntheticMapping.face,
          faceEdgeDot: syntheticMapping.faceEdgeDot,
          faceEdgeType: syntheticMapping.faceEdgeType,
        });
      }
      continue;
    }

    // Fall through to the base-edge no-brep case:
    // - base touching/coplanar-adjacent edges have precomputed facePairs keyed by base volumeEdgeId
    // - synthetic edges are keyed by `${baseId}-a|b`, so they do not hit this map
    const buildTimeGraphIntersection = graphIntersectionsByVolumeEdgeId.get(volumeEdge.volumeEdgeId);
    // This path is for base volume edges where rooms are coplanar-adjacent/touching
    // (so we have precomputed face pairs) but there is no volumetric intersection brep.
    if (buildTimeGraphIntersection && !volumeEdge.brep) {
      for (const facePair of buildTimeGraphIntersection.facePairs) {
        const faceNodeIdA = volA.faceNodeIdByFaceIndex[facePair.aFaceIndex];
        const faceNodeIdB = volB.faceNodeIdByFaceIndex[facePair.bFaceIndex];
        if (!faceNodeIdA || !faceNodeIdB) {
          continue;
        }
        const edgeKey = faceNodeIdA < faceNodeIdB ? `${faceNodeIdA}-${faceNodeIdB}` : `${faceNodeIdB}-${faceNodeIdA}`;
        if (seen.has(edgeKey)) {
          continue;
        }

        seen.add(edgeKey);
        edges.push({
          faceANodeId: faceNodeIdA,
          faceBNodeId: faceNodeIdB,
          face: facePair.overlapFaceInAPlane,
          faceEdgeDot: facePair.faceEdgeDot,
          faceEdgeType: facePair.faceEdgeType,
        });
      }

      continue;
    }

    // Base overlap edges (with `volumeEdge.brep`) intentionally fall through here:
    // they don't emit face edges directly because those relationships are represented
    // by the synthetic `${baseId}-a|b` edges processed above.
    // Any no-brep edge reaching this point means we missed expected face-pair mapping.
    if (!(!!volumeEdge.brep)) {
      console.assert(false, `Unexpected face-edge mapping miss for volumeEdgeId=${volumeEdge.volumeEdgeId}`);
    }
  }

  addOpposingFaceEdgesBetweenIntersectionChildren(edges, seen, volumeNodes, faceNodes);
  return edges;
}

function isIntersectionVolumeNode(volumeNode: BuildingGraphVolumeNode | undefined): boolean {
  return volumeNode?.parentHandle?.type === "VOLUME_EDGE";
}

/**
 * Graph completion for intersection volumes: parent faces with opposing OVERLAP
 * already imply through-wall adjacency; propagate that to their intersection child
 * faces whenever those children still overlap in area.
 */
function addOpposingFaceEdgesBetweenIntersectionChildren(
  edges: BuildingGraphFaceEdge[],
  seen: Set<string>,
  volumeNodes: Table<BuildingGraphVolumeNode>,
  faceNodes: Table<BuildingGraphFaceNode>,
): void {
  const childrenByParentFaceNodeId = new Map<string, string[]>();

  for (const edge of edges) {
    if (edge.faceEdgeType !== "OVERLAP" || edge.faceEdgeDot <= 0) {
      continue;
    }
    const faceNodeA = faceNodes[edge.faceANodeId];
    const faceNodeB = faceNodes[edge.faceBNodeId];
    const volumeA = faceNodeGetVolumeNodeFromTable(faceNodeA, volumeNodes);
    const volumeB = faceNodeGetVolumeNodeFromTable(faceNodeB, volumeNodes);
    const aIsIntersection = isIntersectionVolumeNode(volumeA);
    const bIsIntersection = isIntersectionVolumeNode(volumeB);
    if (aIsIntersection === bIsIntersection) {
      continue;
    }
    const parentFaceNodeId = aIsIntersection ? edge.faceBNodeId : edge.faceANodeId;
    const childFaceNodeId = aIsIntersection ? edge.faceANodeId : edge.faceBNodeId;
    const children = childrenByParentFaceNodeId.get(parentFaceNodeId);
    if (children) {
      children.push(childFaceNodeId);
    } else {
      childrenByParentFaceNodeId.set(parentFaceNodeId, [childFaceNodeId]);
    }
  }

  // Snapshot opposing parent edges before we append child–child edges.
  const opposingParentEdges = edges.filter((edge) => edge.faceEdgeType === "OVERLAP" && edge.faceEdgeDot < 0);

  for (const parentEdge of opposingParentEdges) {
    const childrenA = childrenByParentFaceNodeId.get(parentEdge.faceANodeId);
    const childrenB = childrenByParentFaceNodeId.get(parentEdge.faceBNodeId);
    if (!childrenA || !childrenB) {
      continue;
    }
    for (const childFaceNodeIdA of childrenA) {
      const faceNodeA = faceNodes[childFaceNodeIdA];
      const faceA = getFaceFromFaceNode(faceNodeA, volumeNodes);
      if (!faceNodeA || !faceA) {
        continue;
      }
      for (const childFaceNodeIdB of childrenB) {
        const faceNodeB = faceNodes[childFaceNodeIdB];
        const volumeNodeIdA = faceNodeVolumeParentHandle(faceNodeA)?.volumeNodeId;
        const volumeNodeIdB = faceNodeVolumeParentHandle(faceNodeB)?.volumeNodeId;
        if (!faceNodeB || volumeNodeIdA === volumeNodeIdB) {
          // Same intersection volume: parent-facing faces are already related via that volume.
          continue;
        }
        const edgeKey =
          childFaceNodeIdA < childFaceNodeIdB
            ? `${childFaceNodeIdA}-${childFaceNodeIdB}`
            : `${childFaceNodeIdB}-${childFaceNodeIdA}`;
        if (seen.has(edgeKey)) {
          continue;
        }
        const faceB = getFaceFromFaceNode(faceNodeB, volumeNodes);
        if (!faceB) {
          continue;
        }
        const intersection = computeCoplanarFacePairIntersection(faceA, faceB);
        if (
          !intersection ||
          intersection.faceEdgeType !== "OVERLAP" ||
          intersection.faceEdgeDot >= 0 ||
          !intersection.overlapFaceInAPlane
        ) {
          continue;
        }
        seen.add(edgeKey);
        edges.push({
          faceANodeId: childFaceNodeIdA,
          faceBNodeId: childFaceNodeIdB,
          face: intersection.overlapFaceInAPlane,
          faceEdgeDot: intersection.faceEdgeDot,
          faceEdgeType: "OVERLAP",
        });
      }
    }
  }
}

/**
 * Group face nodes into topologies (connected components via face edges).
 * Each topology is a coplanar set of face nodes; the faceTopology is the planar graph
 * of the union of their face outlines subdivided by face edge boundaries.
 */
function computeFaceTopologies(
  faceEdges: BuildingGraphFaceEdge[],
  faceNodes: Table<BuildingGraphFaceNode>,
  volumeNodes: Table<BuildingGraphVolumeNode>,
): RWTable<BuildingGraphPlanarTopology> {
  const buildingPlaneRegistry = new Map<string, RegisteredBuildingPlane>();
  const faceNodeAdjacencyMap = new Map<string, Set<string>>();

  for (const faceNodeId in faceNodes) {
    faceNodeAdjacencyMap.set(faceNodeId, new Set());
  }

  for (const fe of faceEdges) {
    faceNodeAdjacencyMap.get(fe.faceANodeId)?.add(fe.faceBNodeId);
    faceNodeAdjacencyMap.get(fe.faceBNodeId)?.add(fe.faceANodeId);
  }

  const visited = new Set<string>();
  const componentIdByFaceNodeId = new Map<string, number>();
  const faceNodeIdsByComponent: string[][] = [];
  const topologies: BuildingGraphPlanarTopology[] = [];

  for (const faceNodeId in faceNodes) {
    if (visited.has(faceNodeId)) {
      continue;
    }

    const faceNode = faceNodes[faceNodeId];
    if (!faceNode) {
      continue;
    }

    const componentId = faceNodeIdsByComponent.length;
    const componentFaceNodeIds: string[] = [];
    const faceNodeComponentStack = [faceNode.faceNodeId];

    visited.add(faceNode.faceNodeId);

    // DFS traversal of the face node adjacency map to find all connected face nodes
    while (faceNodeComponentStack.length > 0) {
      const currentFaceNodeId = faceNodeComponentStack.pop();
      assertIsNotNullOrUndefined(currentFaceNodeId);
      componentFaceNodeIds.push(currentFaceNodeId);
      componentIdByFaceNodeId.set(currentFaceNodeId, componentId);

      for (const adjacentFaceNodeId of faceNodeAdjacencyMap.get(currentFaceNodeId) ?? []) {
        if (!visited.has(adjacentFaceNodeId)) {
          visited.add(adjacentFaceNodeId);
          faceNodeComponentStack.push(adjacentFaceNodeId);
        }
      }
    }
    faceNodeIdsByComponent.push(componentFaceNodeIds);
  }

  const faceEdgeIndicesByComponent = new Map<number, number[]>();
  for (let faceEdgeIndex = 0; faceEdgeIndex < faceEdges.length; faceEdgeIndex++) {
    const faceEdge = faceEdges[faceEdgeIndex];
    const componentIdA = faceEdge ? componentIdByFaceNodeId.get(faceEdge.faceANodeId) : undefined;
    const componentIdB = faceEdge ? componentIdByFaceNodeId.get(faceEdge.faceBNodeId) : undefined;
    if (componentIdA === undefined || componentIdB === undefined) {
      continue;
    }
    if (!(componentIdA === componentIdB)) {
      console.assert(false, `Mismatched face-edge components for faceEdgeIndex=${faceEdgeIndex}: ${componentIdA} != ${componentIdB}`);
    }
    if (componentIdA !== componentIdB) {
      continue;
    }

    const componentEdgeIndices = faceEdgeIndicesByComponent.get(componentIdA);
    if (componentEdgeIndices) {
      componentEdgeIndices.push(faceEdgeIndex);
    } else {
      faceEdgeIndicesByComponent.set(componentIdA, [faceEdgeIndex]);
    }
  }

  for (let componentId = 0; componentId < faceNodeIdsByComponent.length; componentId++) {
    const faceNodeIds = faceNodeIdsByComponent[componentId];
    if (!faceNodeIds) {
      continue;
    }

    const firstId = faceNodeIds[0];
    assertIsNotNullOrUndefined(firstId);
    const firstNode = faceNodes[firstId];
    if (!firstNode) {
      continue;
    }

    const refFace = getFaceFromFaceNode(firstNode, volumeNodes);
    if (!refFace || refFace.loops[0].length < NONDEGENERATE_FACE_COUNT) {
      continue;
    }
    const canonicalRefPlane = planeCanonicalFromRefPlane(refFace.plane);
    if (!canonicalRefPlane) {
      continue;
    }
    const { plane: refPlane, planeHash: refPlaneHash } = resolveRegisteredBuildingPlane(
      canonicalRefPlane,
      buildingPlaneRegistry,
    );

    // all the polygons for the face topology
    const faceTopologyPolygons: PolyLine2[] = [];

    const faceNodePlaneItems: BuildingGraphFaceNodePlaneIndexItem[] = [];
    for (const faceNodeId of faceNodeIds) {
      const faceNode = faceNodes[faceNodeId];
      const faceNodeFace = getFaceFromFaceNode(faceNode, volumeNodes);
      if (!faceNodeFace || !faceNode) {
        continue;
      }

      for (const loop of faceNodeFace.loops) {
        if (loop.length >= NONDEGENERATE_FACE_COUNT) {
          const transformed = transformFaceLoopBasis(polyLine2Copy(loop), faceNodeFace.plane, refPlane);
          polyLine2SetCW(transformed);
          faceTopologyPolygons.push(transformed);
          const faceNodePolygonIndex = faceNodePlaneItems.length;
          faceNodePlaneItems.push({
            ...polyLine2AABB2(transformed),
            index: faceNodePolygonIndex,
            faceNodeId,
            polygon: transformed,
          });
        }
      }
    }

    const faceEdgePlaneItems: BuildingGraphFaceEdgePlaneIndexItem[] = [];
    const faceEdgeIndicesInComponent = faceEdgeIndicesByComponent.get(componentId) ?? [];
    for (const faceEdgeIndex of faceEdgeIndicesInComponent) {
      const faceEdge = faceEdges[faceEdgeIndex];
      if (!faceEdge) {
        continue;
      }
      if (!faceEdge.face) {
        continue;
      }
      const faceEdgeOuterLoop = faceEdge.face.loops[0];
      if (faceEdgeOuterLoop && faceEdgeOuterLoop.length >= NONDEGENERATE_FACE_COUNT) {
        const transformed = transformFaceLoopBasis(polyLine2Copy(faceEdgeOuterLoop), faceEdge.face.plane, refPlane);
        polyLine2SetCW(transformed);
        faceTopologyPolygons.push(transformed);
        const faceEdgePolygonIndex = faceEdgePlaneItems.length;
        faceEdgePlaneItems.push({
          ...polyLine2AABB2(transformed),
          index: faceEdgePolygonIndex,
          faceEdgeIndex,
          polygon: transformed,
        });
      }
    }

    if (faceTopologyPolygons.length === 0) {
      continue;
    }

    // create the planar graph for the face topology
    const sortedContributorIds = [...topologyIdContributorFaceNodeIds(faceNodeIds, faceNodes, volumeNodes)].sort();
    const topologyId = `topology-${refPlaneHash}-${sortedContributorIds.join("|")}`;
    const faceTopology = polygonPlanarGraphNew(faceTopologyPolygons, true);

    const faceCount = faceTopology.planarGraph.faceHalfEdges.length;
    const SORT_SNAP = 1e-4;
    const POLE_PRECISION = 1.0;
    const snapRound = (value: number): number => {
      return Math.round(value / SORT_SNAP) * SORT_SNAP;
    };

    interface FaceSortKey {
      readonly planarGraphFaceIndex: number;
      readonly isPerimeter: boolean;
      readonly aabbU: number;
      readonly aabbV: number;
      readonly loop: PolyLine2 | null;
      poleU: number | null;
      poleV: number | null;
    }

    const faceSortKeys: FaceSortKey[] = [];
    for (let planarGraphFaceIndex = 0; planarGraphFaceIndex < faceCount; planarGraphFaceIndex++) {
      const isPerimeter = faceTopology.planarGraph.faceWindingDirs[planarGraphFaceIndex] === WindingDir.CW;
      if (isPerimeter) {
        faceSortKeys.push({
          planarGraphFaceIndex,
          isPerimeter: true,
          aabbU: Infinity,
          aabbV: Infinity,
          loop: null,
          poleU: null,
          poleV: null,
        });
        continue;
      }

      const faceLoop = planarGraphVertexStripToPolyLine2(
        faceTopology.planarGraph,
        faceTopology.planarGraph.faceVertices[planarGraphFaceIndex],
      );
      const faceAabb = polyLine2AABB2(faceLoop);
      faceSortKeys.push({
        planarGraphFaceIndex,
        isPerimeter: false,
        aabbU: snapRound(faceAabb.minX),
        aabbV: snapRound(faceAabb.minY),
        loop: faceLoop,
        poleU: null,
        poleV: null,
      });
    }

    const ensurePole = (faceSortKey: FaceSortKey): void => {
      if (faceSortKey.poleU !== null || faceSortKey.loop === null) {
        return;
      }
      const pole = poleOfInaccessibilityPolyLine2(faceSortKey.loop, POLE_PRECISION);
      if (pole) {
        faceSortKey.poleU = snapRound(pole[0]);
        faceSortKey.poleV = snapRound(pole[1]);
        return;
      }
      faceSortKey.poleU = faceSortKey.aabbU;
      faceSortKey.poleV = faceSortKey.aabbV;
    };

    faceSortKeys.sort((faceSortKeyA, faceSortKeyB) => {
      if (faceSortKeyA.isPerimeter !== faceSortKeyB.isPerimeter) {
        return faceSortKeyA.isPerimeter ? 1 : -1;
      }
      if (faceSortKeyA.aabbU !== faceSortKeyB.aabbU) {
        return faceSortKeyA.aabbU - faceSortKeyB.aabbU;
      }
      if (faceSortKeyA.aabbV !== faceSortKeyB.aabbV) {
        return faceSortKeyA.aabbV - faceSortKeyB.aabbV;
      }

      ensurePole(faceSortKeyA);
      ensurePole(faceSortKeyB);
      const poleUCompare = (faceSortKeyA.poleU ?? 0) - (faceSortKeyB.poleU ?? 0);
      if (poleUCompare !== 0) {
        return poleUCompare;
      }
      const poleVCompare = (faceSortKeyA.poleV ?? 0) - (faceSortKeyB.poleV ?? 0);
      if (poleVCompare !== 0) {
        return poleVCompare;
      }
      return faceSortKeyA.planarGraphFaceIndex - faceSortKeyB.planarGraphFaceIndex;
    });

    const stableToPlanarGraph = faceSortKeys.map((faceSortKey) => faceSortKey.planarGraphFaceIndex);
    const permutedFaceTopology = permutePolygonPlanarGraphFaces(faceTopology, stableToPlanarGraph);

    const faceEdgePlaneRTree = new RBush<BuildingGraphFaceEdgePlaneIndexItem>();
    faceEdgePlaneRTree.load(faceEdgePlaneItems);
    const faceNodePlaneRTree = new RBush<BuildingGraphFaceNodePlaneIndexItem>();
    faceNodePlaneRTree.load(faceNodePlaneItems);

    const planarGraphFaceToEdgeIds: number[][] = [];
    const planarGraphFaceToFaceNodeIds: string[][] = [];
    const { faceVertices, faceWindingDirs } = permutedFaceTopology.planarGraph;
    for (let faceIndex = 0; faceIndex < faceCount; faceIndex++) {
      const edgeIds: number[] = [];
      const nodeIds: string[] = [];

      // skip exterior face - it's not from a source face node or edge
      if (faceWindingDirs[faceIndex] === WindingDir.CW) {
        planarGraphFaceToEdgeIds[faceIndex] = edgeIds;
        planarGraphFaceToFaceNodeIds[faceIndex] = nodeIds;
        continue;
      }

      const faceVerts = faceVertices[faceIndex];
      if (!faceVerts.length) {
        planarGraphFaceToEdgeIds[faceIndex] = edgeIds;
        planarGraphFaceToFaceNodeIds[faceIndex] = nodeIds;
        continue;
      }

      const faceLoop = planarGraphVertexStripToPolyLine2(permutedFaceTopology.planarGraph, faceVerts);
      const faceLoopSearchAabb = polyLine2AABB2(faceLoop);
      aabb2Grow(faceLoopSearchAabb, DIST_EPSILON, DIST_EPSILON);

      const faceEdgeCandidates = faceEdgePlaneRTree.search(faceLoopSearchAabb);
      faceEdgeCandidates.sort((candidateA, candidateB) => candidateA.faceEdgeIndex - candidateB.faceEdgeIndex);
      for (const faceEdgeCandidate of faceEdgeCandidates) {
        const edgeOverlap = polygonIntersection({
          pls: [[faceLoop], [faceEdgeCandidate.polygon]],
        });
        if (edgeOverlap.length > 0) {
          edgeIds.push(faceEdgeCandidate.faceEdgeIndex);
        }
      }

      const faceNodeCandidates = faceNodePlaneRTree.search(faceLoopSearchAabb);
      faceNodeCandidates.sort((candidateA, candidateB) => {
        if (candidateA.faceNodeId !== candidateB.faceNodeId) {
          return candidateA.faceNodeId.localeCompare(candidateB.faceNodeId);
        }
        return candidateA.index - candidateB.index;
      });
      for (const faceNodeCandidate of faceNodeCandidates) {
        const nodeOverlap = polygonIntersection({
          pls: [[faceLoop], [faceNodeCandidate.polygon]],
        });
        if (nodeOverlap.length > 0) {
          nodeIds.push(faceNodeCandidate.faceNodeId);
        }
      }

      // update the maps
      planarGraphFaceToEdgeIds[faceIndex] = edgeIds;
      planarGraphFaceToFaceNodeIds[faceIndex] = nodeIds;
    }

    topologies.push({
      topologyId,
      faceNodeIds,
      plane: refPlane,
      normalToZAxisAngle: firstNode.normalToZAxisAngle,
      planeHash: refPlaneHash,
      planeTopology: permutedFaceTopology,
      planarGraphFaceToFaceEdgeIds: planarGraphFaceToEdgeIds,
      planarGraphFaceToFaceNodeIds,
      faceEdgePlaneRTree,
      faceNodePlaneRTree,
    });
  }

  const result: RWTable<BuildingGraphPlanarTopology> = {};
  for (const t of topologies) {
    result[t.topologyId] = t;
  }
  return result;
}

function computePlanes(faceTopologies: RWTable<BuildingGraphPlanarTopology>): RWTable<BuildingGraphPlane> {
  const topologyIdsByPlaneHash = new Map<string, string[]>();
  for (const topology of Object.values(faceTopologies)) {
    const list = topologyIdsByPlaneHash.get(topology.planeHash) ?? [];
    list.push(topology.topologyId);
    topologyIdsByPlaneHash.set(topology.planeHash, list);
  }

  const planes: RWTable<BuildingGraphPlane> = {};
  for (const [planeHashKey, planeTopologies] of topologyIdsByPlaneHash) {
    const firstTopology = planeTopologies[0] ? faceTopologies[planeTopologies[0]] : undefined;
    const refPlane = firstTopology?.plane;
    if (!refPlane) {
      continue;
    }

    const polylinesInRefPlane: PolyLine2[] = [];
    for (const topologyId of planeTopologies) {
      const topology = faceTopologies[topologyId];
      if (!topology) {
        continue;
      }
      const planarGraph = topology.planeTopology.planarGraph;
      const faceWindingDirs = planarGraph.faceWindingDirs ?? [];
      for (let fi = 0; fi < planarGraph.faceHalfEdges.length; fi++) {
        if (faceWindingDirs[fi] === WindingDir.CW) {
          const loop = planarGraphHalfEdgeStripToPolyLine2(planarGraph, planarGraph.faceHalfEdges[fi]);
          polylinesInRefPlane.push(transformFaceLoopBasis(polyLine2Copy(loop), topology.plane, refPlane));
        }
      }
    }

    const unionAabb = polylinesInRefPlane.length > 0 ? aabb2ByPolyLine2s(polylinesInRefPlane) : null;
    if (!unionAabb) {
      continue;
    }

    const planeFace = faceNew([polyLine2SetCW(polyLine2FromAABB2(unionAabb))], planeCopy(refPlane));

    planes[planeHashKey] = {
      planeHash: planeHashKey,
      planeFace,
      planeTopologyIds: planeTopologies,
    };
  }
  return planes;
}

function getFaceNormalToZAxisAngle(face: Face): number {
  const plane = face.plane;
  const normal = planeNormal(plane);
  return vec3AngleBetween(0, 0, 1, normal[0], normal[1], normal[2]);
}

function getFaceOutwardSideDirection(face: Face): BuildingGraphFaceOutwardSideDirection {
  const canonicalPlane = planeCanonicalFromRefPlane(face.plane);
  if (!canonicalPlane) {
    // Degenerate face planes (e.g. near-zero-area intersection faces) cannot be
    // canonicalized reliably. Keep generation deterministic and non-throwing.
    return "WALL_PLANE_POSITIVE_NORMAL";
  }

  const [faceNx, faceNy, faceNz] = planeNormal(face.plane);
  const [wallNx, wallNy, wallNz] = planeNormal(canonicalPlane);
  const dot = vec3Dot(faceNx, faceNy, faceNz, wallNx, wallNy, wallNz);
  return dot >= 0 ? "WALL_PLANE_POSITIVE_NORMAL" : "WALL_PLANE_NEGATIVE_NORMAL";
}

/**
 * Parent handles on intersection volume nodes store indices into the original
 * volume-edge list produced by computeVolumeEdges(). Keep that list as a
 * prefix when merging, so those indices remain valid and explicit.
 */
function mergeVolumeEdgesPreservingParentIndices(
  baseVolumeEdges: BuildingGraphVolumeEdge[],
  additionalVolumeEdges: BuildingGraphVolumeEdge[],
): BuildingGraphVolumeEdge[] {
  return [...baseVolumeEdges, ...additionalVolumeEdges];
}

function getFaceFromFaceNode(
  faceNode: BuildingGraphFaceNode | undefined,
  volumeNodes: Table<BuildingGraphVolumeNode>,
): Face | null {
  return faceNodeGetFaceFromTable(faceNode, volumeNodes) ?? null;
}

export function getBuildingGraphPlaneTypeFromNormalToZAxisAngle(
  normalToZAxisAngle: number,
  epsilon = DIST_EPSILON,
): BuildingGraphPlaneType {
  if (Math.abs(normalToZAxisAngle - Math.PI / 2) < epsilon) {
    return "VERTICAL"; // face normal perpendicular to +Z axis
  }
  if (Math.abs(normalToZAxisAngle) < epsilon || Math.abs(normalToZAxisAngle - Math.PI) < epsilon) {
    return "HORIZONTAL"; // face normal parallel (or anti-parallel) to +Z axis
  }
  return "OTHER";
}

/**
 * For each face node id, lists indices into {@link BuildingGraph.faceEdges} for edges
 * incident on that node. Omitted keys mean no incident face edges. Sparse holes in
 * `faceEdges` are skipped.
 *
 * @TODO - this could be computed while the graph is being
 * built rather than as a post processing step
 */
function buildFaceNodesToEdgeIds(faceEdges: readonly BuildingGraphFaceEdge[]): Record<string, number[]> {
  const map: Record<string, number[]> = {};
  for (let i = 0; i < faceEdges.length; i++) {
    const faceEdge = faceEdges[i];
    if (!faceEdge) {
      continue;
    }
    const a = faceEdge.faceANodeId;
    const b = faceEdge.faceBNodeId;
    if (map[a] === undefined) {
      map[a] = [];
    }
    if (map[b] === undefined) {
      map[b] = [];
    }
    map[a].push(i);
    map[b].push(i);
  }
  return map;
}
